import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { RateCard } from '@gigdriver/domain';
import {
  getOrgRateCards,
  createRateCard as createRateCardApi,
  updateRateCard as updateRateCardApi,
  deleteRateCard as deleteRateCardApi,
} from '@gigdriver/client';

export const useRateCardsStore = defineStore('rateCards', () => {
  const rateCards = ref<RateCard[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  async function loadOrgRateCards(orgId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      rateCards.value = await getOrgRateCards(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load rate cards';
    } finally {
      isLoading.value = false;
    }
  }

  async function createRateCard(
    orgId: string,
    data: Omit<RateCard, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>,
  ) {
    try {
      error.value = null;
      await createRateCardApi(orgId, data);
      await loadOrgRateCards(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create rate card';
      throw err;
    }
  }

  async function updateRateCard(orgId: string, cardId: string, updates: Partial<RateCard>) {
    try {
      error.value = null;
      await updateRateCardApi(orgId, cardId, updates);
      await loadOrgRateCards(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update rate card';
      throw err;
    }
  }

  async function deleteRateCard(orgId: string, cardId: string) {
    try {
      error.value = null;
      await deleteRateCardApi(orgId, cardId);
      rateCards.value = rateCards.value.filter(c => c.id !== cardId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete rate card';
      throw err;
    }
  }

  return { rateCards, isLoading, error, loadOrgRateCards, createRateCard, updateRateCard, deleteRateCard };
});
